import { useEffect, useRef, useState } from "react";
import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { PrimaryButton as V31PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { PrimaryButton as DeprecatedPrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { Product } from "@~/types/types";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import {
  WISHLIST_GIFT_MESSAGE,
  WISHLIST_GIFT_MESSAGE_ERROR,
  WISHLIST_GIFT_MESSAGE_SUCCESS,
} from "@~/utils/events";
import styles from "./GiftMessageModal.module.scss";
import { useFeatureFlags } from "@~/flags/flags";
import { DeliveryNotice } from "@~/product-card-components/WishlistCallToAction/utils/DeliverNotice/DeliveryNotice";

type GiftMessageModalProps = {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export function GiftMessageModal({
  product,
  isOpen,
  onClose,
  onConfirm,
}: GiftMessageModalProps) {
  const { variant } = useVariant();
  const [from, setFrom] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const listenersRef = useRef<{
    onSuccess: EventListener;
    onError: EventListener;
  } | null>(null);

  const imageUrl = variant?.images?.primary?.split("?")[0] || "";
  const skuID = variant?.aliases?.skuId ?? product.aliases.pimProductId;

  // Clean up listeners and abort signal on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (listenersRef.current) {
        window.removeEventListener(
          WISHLIST_GIFT_MESSAGE_SUCCESS,
          listenersRef.current.onSuccess,
        );
        window.removeEventListener(
          WISHLIST_GIFT_MESSAGE_ERROR,
          listenersRef.current.onError,
        );
      }
    };
  }, []);

  const handleSubmit = () => {
    setIsSubmitting(true);
    setSubmitError(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    function onSuccess(e: Event) {
      if (controller.signal.aborted) return;
      const detail = (e as CustomEvent<{ skuID: string }>).detail;
      if (detail.skuID !== skuID) return;
      cleanup();
      setIsSubmitting(false);
      onConfirm();
    }

    function onError(e: Event) {
      if (controller.signal.aborted) return;
      const detail = (e as CustomEvent<{ skuID: string; message?: string }>)
        .detail;
      if (detail.skuID !== skuID) return;
      cleanup();
      setIsSubmitting(false);
      setSubmitError(
        detail.message ?? "Something went wrong, please try again later.",
      );
    }

    listenersRef.current = { onSuccess, onError };

    const timeoutId = setTimeout(() => {
      if (controller.signal.aborted) return;
      cleanup();
      setIsSubmitting(false);
      setSubmitError("Something went wrong, please try again later.");
    }, 8000);

    function cleanup() {
      controller.abort();
      if (listenersRef.current) {
        window.removeEventListener(
          WISHLIST_GIFT_MESSAGE_SUCCESS,
          listenersRef.current.onSuccess,
        );
        window.removeEventListener(
          WISHLIST_GIFT_MESSAGE_ERROR,
          listenersRef.current.onError,
        );
      }
      clearTimeout(timeoutId);
    }

    window.addEventListener(WISHLIST_GIFT_MESSAGE_SUCCESS, onSuccess);
    window.addEventListener(WISHLIST_GIFT_MESSAGE_ERROR, onError);

    window.dispatchEvent(
      new CustomEvent(WISHLIST_GIFT_MESSAGE, {
        detail: { skuID, from, message },
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Write a message"
      onClose={onClose}
      footer={true}
      primaryButtonProps={{
        submitting: isSubmitting,
      }}
      onPrimaryButtonClick={handleSubmit}
      primaryButtonText={"Add to basket"}
      underlinedButtonText={""}
      className={styles.modal}
    >
      <div className={styles.content}>
        {imageUrl && (
          <div className={styles.imageColumn}>
            <img
              src={imageUrl}
              alt={product.title}
              className={styles.productImage}
              data-testid="gift-message-product-image"
            />
          </div>
        )}
        <div className={styles.formColumn}>
          <p className={styles.description}>
            Let them know who it&apos;s from with a gift message. We&apos;ll add
            it to their list to read when you check out.
          </p>
          <div className={styles.field}>
            <label htmlFor="giftMessageFrom">From</label>
            <input
              id="giftMessageFrom"
              name="giftMessageFrom"
              type="text"
              className={styles.input}
              spellCheck
              autoComplete="name"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="gift-message-from"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="giftMessageBody">Message</label>
            <textarea
              id="giftMessageBody"
              name="giftMessageBody"
              className={styles.textarea}
              spellCheck
              autoComplete="off"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="gift-message-body"
            />
          </div>
          {submitError && (
            <p
              aria-live="assertive"
              className={styles.error}
              data-testid="gift-message-error"
            >
              {submitError}
            </p>
          )}
        </div>
      </div>
      <DeliveryNotice />
    </Modal>
  );
}
